/* ============================================================
   ShoLi — Composition Root
   Wires up all modules, binds cross-module events, bootstraps app.
   ============================================================ */

import { createConfirmDialog } from "./lib/confirm-dialog";
import { createToast } from "./lib/toast";
import { createViewManager, type ViewName } from "./lib/view-manager";
import { createState } from "./state";
import { MenuActions } from "./ui/menu-actions";
import { Renderer } from "./ui/renderer";
import { SettingsUI } from "./ui/settings-ui";

// Default storage for production (tests can override by passing a mock)
const STORAGE = typeof localStorage !== "undefined" ? (localStorage as Storage) : undefined;

const STORAGE_KEYS = {
  items: "sholi_items",
  settings: "sholi_settings",
};

// Forward references — set during composition
let _viewManager: ReturnType<typeof createViewManager>;
let _menuActions: MenuActions;

function openMenu(): void {
  const overlay = document.getElementById("menu-overlay");
  if (!overlay) return;
  const panel = overlay.querySelector(".menu-panel");
  const currentView = _viewManager.getCurrentView();

  overlay.classList.remove("hidden");

  if (panel && currentView !== "checking") {
    panel.classList.add("not-in-checking");
  } else if (panel) {
    panel.classList.remove("not-in-checking");
  }
}

// bfcache support
let _renderOnRestore: (() => void) | null = null;

export function setRenderOnRestore(fn: () => void): void {
  _renderOnRestore = fn;
}

window.addEventListener("pageshow", (event) => {
  if (event.persisted && _renderOnRestore) {
    _renderOnRestore();
  }
});

export function initApp(): void {
  // 1. Create state and utility modules (no circular deps)
  const state = createState(STORAGE_KEYS, STORAGE as Storage);
  state.load();

  const toast = createToast();
  const confirm = createConfirmDialog();

  // 2. Create renderer first — view-manager needs its callbacks
  const renderer = new Renderer(state, toast, confirm);

  // 3. Create view manager with renderer callbacks injected at construction
  _viewManager = createViewManager({
    renderFn: renderer.render,
    setSearchInputValue: (val: string) => {
      const input = renderer.getSearchInput();
      if (input) input.value = val;
    },
  });

  // 4. Create remaining modules that depend on view-manager
  _menuActions = new MenuActions(state, confirm, toast);
  const settingsUI = new SettingsUI(state, _viewManager, toast, confirm);

  // 5. Wire remaining cross-references
  settingsUI.setRenderer(renderer);
  setRenderOnRestore(renderer.render);

  // 6. Initialize all modules
  renderer.init();
  _viewManager.init();
  confirm.init();
  settingsUI.loadIntoUI();

  // 7. Bind module-specific events
  settingsUI.bindSave();
  settingsUI.bindDataView();

  // 8. Initial render
  renderer.render();

  // 9. Display version in settings
  const versionEl = document.getElementById("app-version");
  if (versionEl) {
    versionEl.textContent = `v${__APP_VERSION__} (${__GIT_HASH__})`;
  }

  // 10. Subscribe to state changes
  state.subscribe(() => {
    renderer.render();
  });

  // 11. Bind cross-module event glue
  bindEvents();
}

function bindEvents(): void {
  // Back button
  document.getElementById("btn-back")?.addEventListener("click", () => {
    _viewManager.goBack();
  });

  // View switch button (header)
  document.getElementById("btn-view-switch")?.addEventListener("click", () => {
    const current = _viewManager.getCurrentView();
    if (current === "checking") {
      _viewManager.showView("edit");
    } else if (current === "edit") {
      openMenu();
    }
  });

  // Menu button
  document.getElementById("btn-menu")?.addEventListener("click", openMenu);

  // Menu overlay backdrop close
  document.getElementById("menu-overlay")?.addEventListener("click", (e) => {
    if (e.target === e.currentTarget) {
      document.getElementById("menu-overlay")?.classList.add("hidden");
    }
  });

  // Menu back button
  document.getElementById("btn-menu-back")?.addEventListener("click", () => {
    document.getElementById("menu-overlay")?.classList.add("hidden");
  });

  // Menu navigation items
  document.querySelectorAll<HTMLElement>(".menu-item").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.view) {
        _viewManager.showView(btn.dataset.view as ViewName);
      } else if (btn.dataset.action) {
        _menuActions.handleAction(btn.dataset.action);
      }
      document.getElementById("menu-overlay")?.classList.add("hidden");
    });
  });
}
